import { z } from 'zod';
import { ChannelTypeSchema } from './Channel.js';

export const BoardCategorySchema = z.enum([
  'master_controller',
  'drive_board',
  'io_expander',
  'sensor_module',
  'gateway',
]);

export const ChannelCommProtocolSchema = z.enum([
  'digital_24v',
  'analog_24v',
  'can',
  'modbus_rtu',
  'internal',
]);

export const ConfigParamTypeSchema = z.enum(['int', 'float', 'bool', 'string', 'enum']);

export const ConfigurableParamSchema = z.object({
  name: z.string().min(1),
  display_name: z.string().min(1),
  param_type: ConfigParamTypeSchema,
  default_value: z.string(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  options: z.array(z.string()),
  units: z.string().nullable(),
  description: z.string().nullable(),
});

export const BoardChannelSpecSchema = z.object({
  pin: z.number().int().min(0).max(255),
  default_name: z.string().min(1),
  channel_type: ChannelTypeSchema,
  comm_protocol: ChannelCommProtocolSchema,
  configurable_types: z.array(ChannelTypeSchema),
  voltage_min: z.number().nullable(),
  voltage_max: z.number().nullable(),
  current_min_ma: z.number().nullable(),
  current_max_ma: z.number().nullable(),
  default_scaling_factor: z.number(),
  default_scaling_offset: z.number(),
  default_units: z.string().nullable(),
  configurable_params: z.array(ConfigurableParamSchema),
});

export const BoardCapabilitiesSchema = z.object({
  max_node_id: z.number().int().min(0).max(255),
  supports_can: z.boolean(),
  supports_modbus: z.boolean(),
  supports_ota: z.boolean(),
  max_concurrent_relays: z.number().int().min(0).max(255).nullable(),
  stepper_drivers: z.number().int().min(0).max(255),
  adc_resolution_bits: z.number().int().min(0).max(255),
  has_status_led: z.boolean(),
  // Extended tech specs (all optional — older boards may have them as undefined)
  power_supply_v_min: z.number().nullish(), // e.g. 12
  power_supply_v_max: z.number().nullish(), // e.g. 24
  max_current_a: z.number().nullish(), // total board draw
  power_consumption_w: z.number().nullish(),
  dimensions_mm: z
    .object({
      length: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .nullish(),
  weight_g: z.number().nullish(),
  ip_rating: z.string().nullish(), // e.g. 'IP20', 'IP67'
  mounting: z.string().nullish(), // e.g. 'DIN rail', 'panel mount'
  // I/O summary (denormalized counts for at-a-glance display)
  digital_inputs_count: z.number().int().nullish(),
  digital_outputs_count: z.number().int().nullish(),
  analog_inputs_count: z.number().int().nullish(),
  analog_outputs_count: z.number().int().nullish(),
  pwm_outputs_count: z.number().int().nullish(),
  relay_outputs_count: z.number().int().nullish(),
  motor_drivers_count: z.number().int().nullish(),
  // Drive
  max_pwm_frequency_hz: z.number().nullish(),
  max_drive_current_a: z.number().nullish(),
  motor_types: z.array(z.string()).nullish(), // e.g. ['stepper', 'dc_brushed', 'servo']
  // Comm
  can_buses_count: z.number().int().nullish(),
  rs485_ports_count: z.number().int().nullish(),
  ethernet_ports_count: z.number().int().nullish(),
  usb_ports_count: z.number().int().nullish(),
  has_wifi: z.boolean().nullish(),
  has_bluetooth: z.boolean().nullish(),
  // Free-form notes for anything not captured above
  notes: z.string().nullish(),
});

export const BoardModelSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('board'),
  display_name: z.string().min(1),
  vendor: z.string().min(1),
  category: BoardCategorySchema,
  firmware_min_version: z.string().min(1),
  description: z.string().nullable(),
  channels: z.array(BoardChannelSpecSchema),
  capabilities: BoardCapabilitiesSchema,
  published: z.boolean(),
  is_deprecated: z.boolean().nullish(), // legacy boards stay visible but flagged
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateBoardModelSchema = z.object({
  id: z.string().min(1, 'ID is required (e.g. rhia-myboard-v1)'),
  display_name: z.string().min(1, 'Display name is required'),
  vendor: z.string().min(1, 'Vendor is required'),
  category: BoardCategorySchema,
  firmware_min_version: z.string().min(1),
  description: z.string().nullable(),
  channels: z.array(BoardChannelSpecSchema),
  capabilities: BoardCapabilitiesSchema,
});

export const UpdateBoardModelSchema = z.object({
  display_name: z.string().min(1).optional(),
  vendor: z.string().min(1).optional(),
  firmware_min_version: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  channels: z.array(BoardChannelSpecSchema).optional(),
  capabilities: BoardCapabilitiesSchema.optional(),
  published: z.boolean().optional(),
  is_deprecated: z.boolean().optional(),
});

export type ParsedBoardModel = z.infer<typeof BoardModelSchema>;
export type ParsedBoardChannelSpec = z.infer<typeof BoardChannelSpecSchema>;
export type ParsedBoardCapabilities = z.infer<typeof BoardCapabilitiesSchema>;
export type ParsedConfigurableParam = z.infer<typeof ConfigurableParamSchema>;
export type ParsedCreateBoardModel = z.infer<typeof CreateBoardModelSchema>;
export type ParsedUpdateBoardModel = z.infer<typeof UpdateBoardModelSchema>;
